import React, { useEffect, useState } from 'react'
import {
  AddEventToSummitCategoryDocument,
  EditSummitEventTitleDocument,
  GetCategoryEventDocument,
  Maybe,
  PageType,
} from '~/api/generated/graphql'
import { DraggableProvidedDraggableProps, DraggableProvidedDragHandleProps } from '@hello-pangea/dnd'
import dragDropIcon from '@web/images/posts/DragDrop.svg'
import { elementClicked } from '~/common/EventLogger'
import pencilIcon from '@web/images/community/pencil-icon.svg'
import { Button } from 'react-bootstrap'
import PlainTextInput from '~/common/PlainTextInput'
import { asDate, getFullName, trimFromDelimiters } from '~/utils'
import { SummitTitleErrorType, useSummitCategoryPosts } from '~/contexts/SummitCategoryPostsContext'
import { calculateFieldLengthSeverity } from '~/pages/posts/PostUtils'
import { useWindowSize } from '~/common/hooks/useWindowSize'
import FormError from '~/common/FormError'
import { useAuth } from '~/auth/Auth'
import { useMutation, useQuery } from '@apollo/client'
import { getEventTimeText } from '~/common/TimeAgo'
import { RegisterEventButton } from '~/common/RegisterEventButton'
import { eventHasStartHandler } from '~/common/utils'
import { Link } from 'react-router-dom'

type CategoryEventType = {
  eventId: string
  communityId: string
  title: string
  draft: boolean
  eventStart: string
  eventEnd: string
  hidden: boolean
  summitTitle: string
  livestreamStart: boolean
  createdBy: {
    userId: string
    firstName: string
    lastName: string
    nickName: string
  }
  community: {
    communityId: string
    name: string
  }
}

type CategoryEventProps = {
  eventId: string
  isEditing: boolean
  draggableProps: DraggableProvidedDraggableProps
  dragHandleProps?: Maybe<DraggableProvidedDragHandleProps>
  draggableInnerRef: (element: HTMLElement | null) => void
  isDragging: boolean
  onRemove: () => void
  isEditingSummitTitle: boolean
  releaseId: string
  releaseName: string
  isUnderSubcategory: boolean
  pendingSummitPostIds?: string[]
  setPendingSummitPostIds?: React.Dispatch<React.SetStateAction<string[]>>
  categoryId: string
  postOrder: string[]
}

type CategoryEventRowProps = {
  event: CategoryEventType
  isEditing: boolean
  draggableProps: DraggableProvidedDraggableProps
  dragHandleProps?: Maybe<DraggableProvidedDragHandleProps>
  draggableInnerRef: (element: HTMLElement | null) => void
  isDragging: boolean
  onRemove: () => void
  isEditingSummitTitle: boolean
  releaseId: string
  releaseName: string
  isUnderSubcategory: boolean
  pendingSummitPostIds?: string[]
  setPendingSummitPostIds?: React.Dispatch<React.SetStateAction<string[]>>
  categoryId: string
  postOrder: string[]
}

declare global {
  var scrollPosition: number

  var lastId: string
}

const CategoryEventRow = ({
  event,
  isEditing,
  draggableProps,
  draggableInnerRef,
  dragHandleProps,
  isDragging,
  onRemove,
  isEditingSummitTitle,
  releaseId,
  releaseName,
  isUnderSubcategory,
  pendingSummitPostIds,
  setPendingSummitPostIds,
  categoryId,
  postOrder,
}: CategoryEventRowProps) => {
  const { pageType, editModePostIds, updateEditModeRows, postsWithErrors, updateAllPendingRows, removePostWithError } =
    useSummitCategoryPosts()
  const [editingSummitTitle, setEditingSummitTitle] = useState(isEditingSummitTitle)

  const [isFutureLivestream, setIsFutureLivestream] = useState<boolean>(true)
  const { actingRelAdmin, actingSummitAdmin, isVeevan } = useAuth()

  useEffect(() => {
    return event ? eventHasStartHandler(event, setIsFutureLivestream, isVeevan) : () => {}
  }, [setIsFutureLivestream, event, isVeevan])

  const eventId = event?.eventId || ''
  const postEventId = `{evt}${eventId}`
  const title = event?.title || ''
  const summitTitle = event?.summitTitle || title
  const isSummit = pageType === PageType.Summit
  const justAdded = pendingSummitPostIds?.includes(postEventId)
  const [pendingSummitTitle, setPendingSummitTitle] = useState(
    justAdded ? trimFromDelimiters(summitTitle) : summitTitle
  )
  const titlePhrase = `by ${getFullName(event?.createdBy)} in ${event?.community?.name}`
  const { isCondensed } = useWindowSize()

  const [editSummitTitle] = useMutation(EditSummitEventTitleDocument)
  const canSeeDrafts = pageType == PageType.Release ? actingRelAdmin : actingSummitAdmin

  const showError = postsWithErrors?.has(postEventId)
  const errorMessage =
    editModePostIds?.get(eventId) === SummitTitleErrorType.NO_TITLE
      ? 'Required'
      : 'Press Cancel or Save to complete changes'
  const inPendingEditList = editModePostIds?.has(postEventId)
  const [addEventToSummitCategory] = useMutation(AddEventToSummitCategoryDocument)
  const onSaveSummitTitle = () => {
    console.log('onSaveSummitTitle', postOrder, pendingSummitPostIds, eventId)
    const filteredPostOrder = postOrder.filter(p => p === postEventId || !pendingSummitPostIds?.includes(p))
    if (justAdded) {
      void addEventToSummitCategory({
        variables: {
          categoryId: categoryId,
          eventId: eventId,
          postOrder: filteredPostOrder,
          summitTitle: pendingSummitTitle,
          filter: canSeeDrafts ? {} : { draft: { eq: false } },
          filterEvent: canSeeDrafts ? {} : { draft: { eq: false } },
        },
      }).then(resp => {
        if (resp.data?.addEventToSummitCategory?.ok) {
          removePostWithError?.(postEventId)
          setEditingSummitTitle(false)
          updateEditModeRows?.(postEventId, true)
          updateAllPendingRows?.(postEventId, true)
          setPendingSummitPostIds?.(pendingIds => {
            const newIds = [...pendingIds]
            newIds.splice(newIds.indexOf(postEventId), 1)
            return newIds
          })
        } else {
          removePostWithError?.(postEventId)
        }
      })
    } else {
      void editSummitTitle({ variables: { eventId: eventId, summitTitle: pendingSummitTitle } }).then(resp => {
        if (resp?.data?.editSummitEventTitle?.ok) {
          removePostWithError?.(postEventId)
          setEditingSummitTitle(false)
          updateEditModeRows?.(postEventId, true)
        } else {
          removePostWithError?.(postEventId)
        }
      })
    }
  }

  const onClickEditSummitTitle = () => {
    setEditingSummitTitle(true)
  }

  const onEditSummitTitle = (s: string) => {
    removePostWithError?.(postEventId)
    setPendingSummitTitle(s)

    if (inPendingEditList && s === summitTitle) {
      updateEditModeRows?.(postEventId, true, s ? SummitTitleErrorType.NEW_UNSAVED : SummitTitleErrorType.NO_TITLE)
    } else if (s !== summitTitle) {
      updateEditModeRows?.(postEventId, false, s ? SummitTitleErrorType.NEW_UNSAVED : SummitTitleErrorType.NO_TITLE)
    }
  }

  const onCancel = () => {
    removePostWithError?.(postEventId)
    setEditingSummitTitle(false)
    setPendingSummitTitle(summitTitle.trim())
    updateEditModeRows?.(postEventId, true)
    updateAllPendingRows?.(postEventId, true)
    if (justAdded) {
      setPendingSummitPostIds?.(pendingIds => {
        const newIds = [...pendingIds]
        newIds.splice(newIds.indexOf(postEventId), 1)
        return newIds
      })
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    if (isEditing) {
      e.preventDefault()
      return
    }
    elementClicked(e, `click-event-${isSummit ? 'summit' : 'release'}-view`, {
      communityId: event?.communityId,
      eventId: eventId,
    })
    globalThis.scrollPosition = document.body.children[0].scrollTop
    globalThis.lastId = releaseId
  }
  const getDraftIndicator = () => {
    return <div className={'draft-indicator'}>DRAFT</div>
  }
  const MAX_TITLE_LENGTH = 100
  const titleLength = (pendingSummitTitle || summitTitle).length
  const titleSeverity = editingSummitTitle ? calculateFieldLengthSeverity(titleLength, MAX_TITLE_LENGTH) : ''

  return isEditing ? (
    <div
      className={`event-row-container${isDragging ? ' dragging' : ''}${isSummit ? ' summit' : ''}${
        isUnderSubcategory ? ' indented' : ''
      }${isCondensed ? ' condensed' : ''}`}
      ref={draggableInnerRef}
      {...draggableProps}
      onClick={handleClick}
    >
      <div className={`event-row ${isEditing ? 'editing' : ''}`}>
        <img
          alt={isEditing ? 'reorder drag handle' : ''}
          src={dragDropIcon}
          {...dragHandleProps}
          className={'drag-handle'}
          data-testid={'dnd-handle-event'}
        />
        {event?.draft && <div>{getDraftIndicator()}</div>}
        <div className="event-time">{getEventTimeText(asDate(event?.eventStart), asDate(event?.eventEnd), true)}</div>
        {isSummit &&
          (editingSummitTitle ? (
            <div className={'plain-text-input-container'}>
              <PlainTextInput
                placeholder={'Add a summit title for the event'}
                onChange={onEditSummitTitle}
                value={pendingSummitTitle}
                className={`${isCondensed ? 'condensed' : ''} ${showError ? 'summit-title-error' : ''}`}
              />
              <span
                className={`summit-character-counter ${isCondensed ? 'condensed' : ''} ` + titleSeverity}
                id={'summit-character-counter'}
              >
                {titleLength < 10 ? `0${titleLength}` : titleLength} / {MAX_TITLE_LENGTH}
              </span>
            </div>
          ) : (
            <div className={'summit-title-container'}>
              <span className={`event-title editing`}>{summitTitle}</span>
            </div>
          ))}
        <div className={'content-title-container editing'}>
          <span
            className={`event-title ${!event?.draft && 'editing'}${event?.hidden ? ' hidden' : ''} ${
              isSummit ? 'event-title-on-summit' : ''
            }`}
          >
            {title}
          </span>
          {isSummit && <span className={'title-phrase'}> {titlePhrase}</span>}
        </div>
        <div className={'category-event-edit-buttons'}>
          {editingSummitTitle ? (
            <>
              <Button variant="light" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={onSaveSummitTitle}
                disabled={!pendingSummitTitle || titleLength > MAX_TITLE_LENGTH}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              {pageType === PageType.Summit && (
                <button
                  onClick={onClickEditSummitTitle}
                  className={'category-edit-event'}
                  data-testid={'category-edit-event'}
                >
                  <img alt={'edit'} src={pencilIcon} data-testid={'edit-event-pencil'} />
                </button>
              )}
              <button onClick={onRemove} data-testid={'category-remove-event'}>
                X
              </button>
            </>
          )}
        </div>
      </div>
      {showError && <FormError message={errorMessage} />}
    </div>
  ) : (
    <div
      className={`event-row-container${isDragging ? ' dragging' : ''}${isUnderSubcategory ? ' indented' : ''}${
        isCondensed ? ' condensed' : ''
      }`}
    >
      <div className={`event-row`} ref={draggableInnerRef} {...draggableProps}>
        <img
          alt={isEditing ? 'reorder drag handle' : ''}
          src={dragDropIcon}
          {...dragHandleProps}
          className={'drag-handle hidden'}
        />
        {event?.draft && <div className={'draft-indicator'}>DRAFT</div>}
        <Link
          className={'event-title-container'}
          onClick={handleClick}
          to={`/communities/${event?.communityId}/events/${event?.eventId}`}
          state={{ fromReleaseName: releaseName, fromReleaseId: releaseId }}
        >
          <div className={`event-title${event?.hidden ? ' hidden' : ''}`}>
            {pageType === PageType.Summit ? summitTitle : title}
          </div>
          <div className="event-time">{getEventTimeText(asDate(event?.eventStart), asDate(event?.eventEnd), true)}</div>
        </Link>
        <div className={'event-button'}>
          <RegisterEventButton
            releaseId={releaseId}
            releaseName={releaseName}
            eventId={eventId}
            isFutureLivestream={isFutureLivestream}
          />
        </div>
      </div>
    </div>
  )
}

const CategoryEvent = ({
  eventId,
  isEditing,
  draggableProps,
  draggableInnerRef,
  dragHandleProps,
  isDragging,
  onRemove,
  isEditingSummitTitle,
  releaseId,
  releaseName,
  isUnderSubcategory,
  setPendingSummitPostIds,
  categoryId,
  postOrder,
  pendingSummitPostIds,
}: CategoryEventProps) => {
  const { data, loading } = useQuery(GetCategoryEventDocument, { variables: { id: eventId } })
  const event = {
    eventId: data?.event?.eventId ?? '',
    communityId: data?.event?.communityId ?? '',
    title: data?.event?.title ?? '',
    summitTitle: data?.event?.summitTitle ?? '',
    hidden: Boolean(data?.event?.hidden),
    eventStart: data?.event?.eventStart,
    eventEnd: data?.event?.eventEnd,
    draft: data?.event?.draft ?? false,
    createdBy: {
      userId: data?.event?.createdBy?.userId ?? '',
      firstName: data?.event?.createdBy?.firstName ?? '',
      lastName: data?.event?.createdBy?.lastName ?? '',
      nickName: data?.event?.createdBy?.nickName ?? '',
    },
    community: {
      communityId: data?.event?.community?.communityId ?? '',
      name: data?.event?.community?.name ?? '',
    },
  } as CategoryEventType

  if (loading && !data) {
    return <></>
  }

  return (
    <CategoryEventRow
      event={event}
      isEditing={isEditing}
      isDragging={isDragging}
      onRemove={onRemove}
      isEditingSummitTitle={isEditingSummitTitle}
      releaseId={releaseId}
      releaseName={releaseName}
      isUnderSubcategory={isUnderSubcategory}
      draggableProps={draggableProps}
      draggableInnerRef={draggableInnerRef}
      dragHandleProps={dragHandleProps}
      setPendingSummitPostIds={setPendingSummitPostIds}
      categoryId={categoryId}
      postOrder={postOrder}
      pendingSummitPostIds={pendingSummitPostIds}
    />
  )
}

export default CategoryEvent
